/**
 * /admin/v1/playbook/* API 客户端 · M03 方案管理（S2 Day 7b）
 *
 * 承接：
 * - 后端 schema：apps/api/src/csa_api/admin/playbook_admin.py（Day 7a 落地 · 7 endpoint）
 * - SK 静态目录：apps/api/src/csa_api/admin/_skill_catalog.py（多选组件用）
 *
 * 类型来源：与 M02 同 · 临时手写 · S2 PR-X 切 openapi-typescript（DAC-07 §3.A4 + DAC-08 拟同记）
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiClient } from "@/lib/api-client";

// ─── DTO（与后端 Pydantic 同源） ─────────────────────────────────────────

export interface SkillCatalogItem {
	skill_id: string;
	name: string;
	description: string;
	category: "shopify_admin" | "rag_qa" | "handoff" | "outreach" | "utility";
}

export interface SkillsCatalogResponse {
	items: SkillCatalogItem[];
	total: number;
}

/** T1 标签组合触发条件（五维 ontology · 与后端 PlaybookMatchTags 同源） */
export interface PlaybookMatchTags {
	intent_tags: string[];
	product_tags: string[];
	sale_stage: string[];
	order_status: string[];
	dialogue_state: string[];
}

export interface PlaybookListItem {
	playbook_id: string;
	name: string;
	version: string;
	intent_bindings: string[];
	allowed_skills_count: number;
	step_budget: number;
	has_pre_fetch: boolean;
	branch_count: number;
	branch_names: string[];
	/** T1 命中触发条件 · null = 该 PB 未启用标签召回，走 RoutingTable 快路径 */
	match_tags?: PlaybookMatchTags | null;
}

export interface PlaybookListResponse {
	items: PlaybookListItem[];
	total: number;
}

export interface PlaybookYamlGetResponse {
	playbook_id: string;
	yaml_text: string;
	parsed_version: string;
	parsed_name: string;
}

export interface PlaybookYamlPutRequest {
	yaml_text: string;
	expected_version?: string | null;
}

export interface PlaybookYamlPutResponse {
	playbook_id: string;
	parsed_version: string;
	cache_invalidated: boolean;
}

export interface IntentMappingGetResponse {
	yaml_text: string;
	bundle_version: string;
	intents_with_mapping: number;
}

export interface IntentMappingPutRequest {
	yaml_text: string;
	expected_version?: string | null;
}

export interface IntentMappingPutResponse {
	bundle_version: string;
	cache_invalidated: boolean;
	unknown_intents: string[];
}

export interface RoutingTableGetResponse {
	yaml_text: string;
	bundle_version: string;
	enabled_rules: number;
	short_circuit_rules: number;
	branch_rules: number;
	fallback_to_intent_playbook_mapping: boolean;
}

export interface RoutingTablePutRequest {
	yaml_text: string;
	expected_version?: string | null;
}

export interface RoutingTablePutResponse {
	bundle_version: string;
	cache_invalidated: boolean;
	unknown_intents: string[];
}

export interface PreFetchSpecParsed {
	auto_invoke_if_extractable: Array<{
		skill_id: string;
		trigger_slots: string[];
		trigger_mode: "any" | "all";
	}>;
	rag_namespaces: string[];
	top_k: number;
}

export interface ValidatePrefetchResponse {
	valid: boolean;
	parsed: PreFetchSpecParsed | null;
	errors: string[];
}

// ─── Hooks ────────────────────────────────────────────────────────────────

export function useSkillsCatalog() {
	return useQuery<SkillsCatalogResponse>({
		queryKey: ["admin", "playbook", "skills_catalog"],
		queryFn: async () => {
			const { data } = await apiClient.get<SkillsCatalogResponse>(
				"/admin/v1/playbook/skills_catalog",
			);
			return data;
		},
		staleTime: 60 * 60 * 1000, // 静态目录 · 1h staleTime
	});
}

export function usePlaybookList() {
	return useQuery<PlaybookListResponse>({
		queryKey: ["admin", "playbook", "list"],
		queryFn: async () => {
			const { data } = await apiClient.get<PlaybookListResponse>("/admin/v1/playbook/list");
			return data;
		},
	});
}

export function usePlaybookYaml(pbId: string | null) {
	return useQuery<PlaybookYamlGetResponse>({
		queryKey: ["admin", "playbook", "yaml", pbId],
		queryFn: async () => {
			if (!pbId) throw new Error("pb_id is required");
			const { data } = await apiClient.get<PlaybookYamlGetResponse>(
				`/admin/v1/playbook/${encodeURIComponent(pbId)}/yaml`,
			);
			return data;
		},
		enabled: Boolean(pbId),
	});
}

export function usePutPlaybookYaml(pbId: string) {
	const queryClient = useQueryClient();
	return useMutation<PlaybookYamlPutResponse, unknown, PlaybookYamlPutRequest>({
		mutationFn: async (body) => {
			const { data } = await apiClient.put<PlaybookYamlPutResponse>(
				`/admin/v1/playbook/${encodeURIComponent(pbId)}/yaml`,
				body,
			);
			return data;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["admin", "playbook", "list"] });
			queryClient.invalidateQueries({ queryKey: ["admin", "playbook", "yaml", pbId] });
		},
	});
}

export function useIntentMapping() {
	return useQuery<IntentMappingGetResponse>({
		queryKey: ["admin", "playbook", "intent_mapping"],
		queryFn: async () => {
			const { data } = await apiClient.get<IntentMappingGetResponse>(
				"/admin/v1/playbook/intent_mapping",
			);
			return data;
		},
	});
}

export function usePutIntentMapping() {
	const queryClient = useQueryClient();
	return useMutation<IntentMappingPutResponse, unknown, IntentMappingPutRequest>({
		mutationFn: async (body) => {
			const { data } = await apiClient.put<IntentMappingPutResponse>(
				"/admin/v1/playbook/intent_mapping",
				body,
			);
			return data;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["admin", "playbook", "intent_mapping"] });
		},
	});
}

export function useRoutingTable() {
	return useQuery<RoutingTableGetResponse>({
		queryKey: ["admin", "playbook", "routing_table"],
		queryFn: async () => {
			const { data } = await apiClient.get<RoutingTableGetResponse>(
				"/admin/v1/playbook/routing_table",
			);
			return data;
		},
	});
}

export function usePutRoutingTable() {
	const queryClient = useQueryClient();
	return useMutation<RoutingTablePutResponse, unknown, RoutingTablePutRequest>({
		mutationFn: async (body) => {
			const { data } = await apiClient.put<RoutingTablePutResponse>(
				"/admin/v1/playbook/routing_table",
				body,
			);
			return data;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["admin", "playbook", "routing_table"] });
		},
	});
}

export function useValidatePrefetch() {
	return useMutation<ValidatePrefetchResponse, unknown, { pre_fetch_spec_yaml: string }>({
		mutationFn: async (body) => {
			const { data } = await apiClient.post<ValidatePrefetchResponse>(
				"/admin/v1/playbook/validate-prefetch",
				body,
			);
			return data;
		},
	});
}
